import React, { useState, useRef } from 'react';
import {
  validateClientImport,
  validateWorkerImport,
  importClients,
  importWorkers,
  getImportTemplate,
  getImportSample
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Users,
  Building2,
  Loader2,
  RefreshCw,
  Info,
  FileText,
  ArrowRight,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const IMPORT_STEPS = ['Upload', 'Validate', 'Review', 'Import'];

export default function BulkImportPage() {
  const [activeTab, setActiveTab] = useState('clients');
  const [currentStep, setCurrentStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // File state
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Validation results
  const [validationResults, setValidationResults] = useState(null);
  
  // Import options
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [updateDuplicates, setUpdateDuplicates] = useState(false);
  
  // Import results
  const [importResults, setImportResults] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setValidationResults(null);
      setImportResults(null);
      setCurrentStep(1);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setValidationResults(null);
      setImportResults(null);
      setCurrentStep(1);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setValidating(true);
    try {
      const validateFn = activeTab === 'clients' ? validateClientImport : validateWorkerImport;
      const response = await validateFn(selectedFile);
      setValidationResults(response.data);
      setCurrentStep(2);
      
      const { valid_rows, invalid_rows, duplicate_rows } = response.data;
      if (valid_rows.length > 0) {
        toast.success(`Validation complete: ${valid_rows.length} valid rows found`);
      } else {
        toast.warning('No valid rows found in the file');
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Failed to validate file');
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!validationResults || validationResults.valid_rows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    try {
      const importFn = activeTab === 'clients' ? importClients : importWorkers;
      
      // Include duplicates if updating
      let rowsToImport = [...validationResults.valid_rows];
      if (updateDuplicates) {
        rowsToImport = [...rowsToImport, ...validationResults.duplicate_rows];
      }
      
      const response = await importFn({
        rows: rowsToImport,
        skip_duplicates: skipDuplicates,
        update_duplicates: updateDuplicates
      });
      
      setImportResults(response.data);
      setCurrentStep(3);
      toast.success(`Import complete: ${response.data.imported} new, ${response.data.updated} updated`);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import data');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await getImportTemplate(activeTab);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeTab}_import_template.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await getImportSample(activeTab);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeTab}_sample_data.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Sample file downloaded - contains 15 example records');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download sample file');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setValidationResults(null);
    setImportResults(null);
    setCurrentStep(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    handleReset();
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Bulk Import
          </h1>
          <p className="text-muted-foreground mt-1">
            Import clients and workers from CSV files with validation preview
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
            <Download className="w-4 h-4" />
            Empty Template
          </Button>
          <Button variant="default" onClick={handleDownloadSample} className="gap-2 bg-gradient-to-r from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]">
            <FileSpreadsheet className="w-4 h-4" />
            Sample Data
          </Button>
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {IMPORT_STEPS.map((step, idx) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors',
                    currentStep >= idx
                      ? 'bg-gradient-to-r from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)] text-white'
                      : 'bg-muted text-muted-foreground'
                  )}>
                    {currentStep > idx ? <Check className="w-5 h-5" /> : idx + 1}
                  </div>
                  <span className={cn(
                    'mt-2 text-sm font-medium',
                    currentStep >= idx ? 'text-foreground' : 'text-muted-foreground'
                  )}>
                    {step}
                  </span>
                </div>
                {idx < IMPORT_STEPS.length - 1 && (
                  <div className={cn(
                    'flex-1 h-1 mx-4 rounded',
                    currentStep > idx ? 'bg-gradient-to-r from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]' : 'bg-muted'
                  )} />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Entity Type Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="clients" className="gap-2">
            <Building2 className="w-4 h-4" />
            Import Clients
          </TabsTrigger>
          <TabsTrigger value="workers" className="gap-2">
            <Users className="w-4 h-4" />
            Import Workers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-6">
          {/* Sample Info Alert */}
          <Alert className="mb-6">
            <Info className="w-4 h-4" />
            <AlertTitle>Download Sample Files</AlertTitle>
            <AlertDescription>
              <p className="mb-2">Use the buttons above to download:</p>
              <ul className="list-disc pl-4 space-y-1 text-sm">
                <li><strong>Empty Template</strong> - A blank CSV with headers only, ready for your data</li>
                <li><strong>Sample Data</strong> - A CSV with 15 example {activeTab} records to see the expected format</li>
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                Required fields for Clients: trade_name, client_type | Required fields for Workers: full_name
              </p>
            </AlertDescription>
          </Alert>
          
          <ImportContent
            entityType="clients"
            entityLabel="Clients"
            selectedFile={selectedFile}
            fileInputRef={fileInputRef}
            validationResults={validationResults}
            importResults={importResults}
            currentStep={currentStep}
            uploading={uploading}
            validating={validating}
            importing={importing}
            skipDuplicates={skipDuplicates}
            updateDuplicates={updateDuplicates}
            onFileSelect={handleFileSelect}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onValidate={handleValidate}
            onImport={handleImport}
            onReset={handleReset}
            onSkipDuplicatesChange={setSkipDuplicates}
            onUpdateDuplicatesChange={setUpdateDuplicates}
          />
        </TabsContent>

        <TabsContent value="workers" className="mt-6">
          {/* Sample Info Alert */}
          <Alert className="mb-6">
            <Info className="w-4 h-4" />
            <AlertTitle>Download Sample Files</AlertTitle>
            <AlertDescription>
              <p className="mb-2">Use the buttons above to download:</p>
              <ul className="list-disc pl-4 space-y-1 text-sm">
                <li><strong>Empty Template</strong> - A blank CSV with headers only, ready for your data</li>
                <li><strong>Sample Data</strong> - A CSV with 30 example {activeTab} records to see the expected format</li>
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                Required fields for Workers: full_name | Optional: client_name (must match existing client)
              </p>
            </AlertDescription>
          </Alert>
          
          <ImportContent
            entityType="workers"
            entityLabel="Workers"
            selectedFile={selectedFile}
            fileInputRef={fileInputRef}
            validationResults={validationResults}
            importResults={importResults}
            currentStep={currentStep}
            uploading={uploading}
            validating={validating}
            importing={importing}
            skipDuplicates={skipDuplicates}
            updateDuplicates={updateDuplicates}
            onFileSelect={handleFileSelect}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onValidate={handleValidate}
            onImport={handleImport}
            onReset={handleReset}
            onSkipDuplicatesChange={setSkipDuplicates}
            onUpdateDuplicatesChange={setUpdateDuplicates}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ImportContent({
  entityType,
  entityLabel,
  selectedFile,
  fileInputRef,
  validationResults,
  importResults,
  currentStep,
  uploading,
  validating,
  importing,
  skipDuplicates,
  updateDuplicates,
  onFileSelect,
  onDragOver,
  onDrop,
  onValidate,
  onImport,
  onReset,
  onSkipDuplicatesChange,
  onUpdateDuplicatesChange
}) {
  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>1. Upload CSV File</CardTitle>
          <CardDescription>
            Upload a CSV file containing {entityLabel.toLowerCase()} data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
              selectedFile ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'
            )}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={onFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            {selectedFile ? (
              <div className="space-y-2">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-primary" />
                <p className="font-medium">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onReset(); }}>
                  Choose Different File
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                <p className="font-medium">Drop your CSV file here or click to browse</p>
                <p className="text-sm text-muted-foreground">
                  Supports .csv files only
                </p>
              </div>
            )}
          </div>

          {selectedFile && currentStep === 1 && (
            <div className="mt-4 flex justify-end">
              <Button onClick={onValidate} disabled={validating} className="gap-2">
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Validate File
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validation Results */}
      {validationResults && (
        <Card>
          <CardHeader>
            <CardTitle>2. Validation Results</CardTitle>
            <CardDescription>
              Review the validation results before importing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-2xl font-bold">{validationResults.total_rows}</p>
                <p className="text-sm text-muted-foreground">Total Rows</p>
              </div>
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-center">
                <p className="text-2xl font-bold text-emerald-600">{validationResults.valid_rows.length}</p>
                <p className="text-sm text-muted-foreground">Valid</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-center">
                <p className="text-2xl font-bold text-amber-600">{validationResults.duplicate_rows.length}</p>
                <p className="text-sm text-muted-foreground">Duplicates</p>
              </div>
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 text-center">
                <p className="text-2xl font-bold text-red-600">{validationResults.invalid_rows.length}</p>
                <p className="text-sm text-muted-foreground">Invalid</p>
              </div>
            </div>

            {/* Valid Rows Preview */}
            {validationResults.valid_rows.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Valid Rows ({validationResults.valid_rows.length})
                </h4>
                <ScrollArea className="h-[200px] border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-16">Row</TableHead>
                        {validationResults.headers.slice(0, 5).map(h => (
                          <TableHead key={h}>{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validationResults.valid_rows.slice(0, 10).map((item) => (
                        <TableRow key={item.row}>
                          <TableCell className="font-mono">{item.row}</TableCell>
                          {validationResults.headers.slice(0, 5).map(h => (
                            <TableCell key={h} className="max-w-[150px] truncate">
                              {item.data[h] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
                {validationResults.valid_rows.length > 10 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Showing first 10 of {validationResults.valid_rows.length} valid rows
                  </p>
                )}
              </div>
            )}

            {/* Duplicate Rows */}
            {validationResults.duplicate_rows.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Duplicate Rows ({validationResults.duplicate_rows.length})
                </h4>
                <Alert className="mb-3">
                  <Info className="w-4 h-4" />
                  <AlertTitle>Duplicates Found</AlertTitle>
                  <AlertDescription>
                    These records already exist in the system. You can choose to skip or update them.
                  </AlertDescription>
                </Alert>
                <ScrollArea className="h-[150px] border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50 dark:bg-amber-950/30">
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Existing Code</TableHead>
                        {validationResults.headers.slice(0, 3).map(h => (
                          <TableHead key={h}>{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validationResults.duplicate_rows.map((item) => (
                        <TableRow key={item.row}>
                          <TableCell className="font-mono">{item.row}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.existing_code}</Badge>
                          </TableCell>
                          {validationResults.headers.slice(0, 3).map(h => (
                            <TableCell key={h} className="max-w-[150px] truncate">
                              {item.data[h] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}

            {/* Invalid Rows */}
            {validationResults.invalid_rows.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  Invalid Rows ({validationResults.invalid_rows.length})
                </h4>
                <ScrollArea className="h-[150px] border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-red-50 dark:bg-red-950/30">
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Errors</TableHead>
                        {validationResults.headers.slice(0, 3).map(h => (
                          <TableHead key={h}>{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validationResults.invalid_rows.map((item) => (
                        <TableRow key={item.row}>
                          <TableCell className="font-mono">{item.row}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {item.errors.map((err, i) => (
                                <Badge key={i} variant="destructive" className="text-xs">
                                  {err}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          {validationResults.headers.slice(0, 3).map(h => (
                            <TableCell key={h} className="max-w-[150px] truncate">
                              {item.data[h] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}

            {/* Import Options */}
            {validationResults.valid_rows.length > 0 && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium">Import Options</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Skip Duplicates</Label>
                    <p className="text-sm text-muted-foreground">
                      Skip rows that already exist in the system
                    </p>
                  </div>
                  <Switch
                    checked={skipDuplicates}
                    onCheckedChange={(checked) => {
                      onSkipDuplicatesChange(checked);
                      if (checked) onUpdateDuplicatesChange(false);
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Update Duplicates</Label>
                    <p className="text-sm text-muted-foreground">
                      Update existing records with new data from file
                    </p>
                  </div>
                  <Switch
                    checked={updateDuplicates}
                    onCheckedChange={(checked) => {
                      onUpdateDuplicatesChange(checked);
                      if (checked) onSkipDuplicatesChange(false);
                    }}
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={onReset}>
                Start Over
              </Button>
              <Button
                onClick={onImport}
                disabled={importing || validationResults.valid_rows.length === 0}
                className="gap-2 bg-gradient-to-r from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]"
                data-testid="import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Import {validationResults.valid_rows.length + (updateDuplicates ? validationResults.duplicate_rows.length : 0)} {entityLabel}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Results */}
      {importResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-center">
                <p className="text-2xl font-bold text-emerald-600">{importResults.imported}</p>
                <p className="text-sm text-muted-foreground">New Records</p>
              </div>
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 text-center">
                <p className="text-2xl font-bold text-blue-600">{importResults.updated}</p>
                <p className="text-sm text-muted-foreground">Updated</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-center">
                <p className="text-2xl font-bold text-amber-600">{importResults.skipped}</p>
                <p className="text-sm text-muted-foreground">Skipped</p>
              </div>
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 text-center">
                <p className="text-2xl font-bold text-red-600">{importResults.errors?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Errors</p>
              </div>
            </div>

            {importResults.errors?.length > 0 && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>Some rows failed to import</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 mt-2">
                    {importResults.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>Row {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <Button onClick={onReset} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Import More
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
